/**
 * Performs a deep equality check between two values
 * Handles circular references and prevents infinite recursion
 *
 * @param a First value to compare
 * @param b Second value to compare
 * @returns True if values are deeply equal, false otherwise
 */
export function deepEquals<T>(a: T, b: T): boolean {
  // Use WeakMap to track already compared objects to handle circular references
  return deepEqualsWithVisited(a, b, new WeakMap(), new WeakMap());
}

/**
 * Internal helper function that performs deep equality check with visited object tracking
 *
 * @param a First value to compare
 * @param b Second value to compare
 * @param visitedA Map of already visited objects in the first object
 * @param visitedB Map of already visited objects in the second object
 * @returns True if values are deeply equal, false otherwise
 */
function deepEqualsWithVisited<T>(a: T, b: T, visitedA: WeakMap<object, boolean>, visitedB: WeakMap<object, boolean>): boolean {
  // Handle primitive types and referential equality
  if (a === b) {
    return true;
  }

  // If either is null or not an object, they're not equal
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }

  // Check for circular references
  if (visitedA.has(a as object) || visitedB.has(b as object)) {
    // If we've seen both objects before at the same position, consider them equal
    return visitedA.has(a as object) && visitedB.has(b as object);
  }

  // Mark objects as visited
  visitedA.set(a as object, true);
  visitedB.set(b as object, true);

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle RegExp objects
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.toString() === b.toString();
  }

  // Handle Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; i++) {
      if (!deepEqualsWithVisited(a[i], b[i], visitedA, visitedB)) {
        return false;
      }
    }

    return true;
  }

  // Handle Objects
  if (Object.prototype.toString.call(a) === '[object Object]') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b as object);

    if (keysA.length !== keysB.length) {
      return false;
    }

    // Check if all keys in a exist in b and have equal values
    return keysA.every(
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      (key) => Object.hasOwn(b, key) && deepEqualsWithVisited((a as any)[key], (b as any)[key], visitedA, visitedB),
    );
  }

  // If we get here, values are not equal
  return false;
}
